<template>
  <div class="creativeUnitTable">
    <slot-table id="creativeUnitTable" class="creativeUnitTable__table table-bordered scrollTable tableFix"
      :columns="columnData" :data="tableData" :editable="true" :sortable="true"
      :allowedSortColumns="tableAllowedSortColumns" :activeSortData="tableActiveSortData"
      @toggleCheckboxes="toggleParentCheckboxes" @onAddSort="addSort" @dispatchSortEvent="dispatchSortEvent">

      <template slot="allCheckboxes" slot-scope="props">
        <span>
          <checkbox :id="`checkbox_${props.item.id}`" @selectCheckboxes="groupSelectCheckboxes($event)"
            v-model="props.item.selected" :value="props.item.selected" />
        </span>
      </template>

      <template slot="creativeActions" slot-scope="props">
        <a v-if="(creative.type !== 'after-effects') && props.item.previewUrl" title="Preview Unit"
          class="btn btn-primary btn-xs" :data-type="creative.type" :data-previewId="props.item.id"
          @click.prevent="launchPreview" href="#" target="_blank">
          <i class="fa fa-eye"></i>
        </a>
        <span v-if="creative.type === 'after-effects'">
          <a v-if="aePreviewUrl(props.item.previewUrl)" title="Preview Unit" class="btn btn-primary btn-xs"
            :data-offset="0" :data-type="creative.type" :data-previewId="props.item.id" @click.prevent="launchPreview"
            href="#" target="_blank">
            <i class="fa fa-eye"></i>
          </a>
        </span>
        <router-link title="Single Unit Edit" v-if="!props.item.isNew && !props.item.readOnly" class="btn btn-info btn-xs"
          :to="{ path: `/creatives/${creative.hash_id}/single/${props.item.id}` }">
          <i class="fa fa-th" aria-hidden="true"></i>
        </router-link>
        <button-cta title="Duplicate" data-modalType="copyCreativeUnit" :data-modalID="props.item.id" :onClick="copyRow"
          class="btn btn-success btn-xs" :data-modalField="props.item.locale">
          <i class="fa fa-clone" aria-hidden="true"></i>
        </button-cta>
        <!--        button fixed-->
        <slot v-if="shouldShowCallToActions(props.item, true)">
          <button-cta :title="props.item.editing ? 'Save Unit' : 'Inline Edit'" :onClick="editRow"
            :data-rowId="props.item.id" :data-isNew="props.item.isNew"
            :class="`btn btn-${props.item.editing ? 'success' : 'warning'} btn-xs`">
            <i :class="`fa fa-${props.item.editing ? 'check' : 'edit'}`" aria-hidden="true"></i>
          </button-cta>
          <button-cta :title="props.item.editing ? 'Cancel Changes' : 'Delete Unit'" data-modalType="deleteCreativeUnit"
            :data-modalID="props.item.id" :onClick="props.item.editing ? cancelRow : deleteRow"
            class="btn btn-danger btn-xs">
            <i :class="`fa fa-${props.item.editing ? 'times' : 'trash'}`" aria-hidden="true"></i>
          </button-cta>
        </slot>
        <slot v-else>
          <button-cta v-if="props.item.status !== 'under_review'" :title="'change status to edited'"
            data-modalType="changeStatusToEdited" :data-modalID="props.item.id" :onClick="onStatusChangeToEdited"
            class="btn btn-warning btn-xs sxm">
            <i :class="`fa fa-pencil`" aria-hidden="true"></i>
          </button-cta>
        </slot>
      </template>

      <template slot="locale" slot-scope="props">
        <span v-if="props.item.editing">
          <field :value="props.item.locale" v-model="props.item.locale" />
        </span>
        <span v-else>{{ props.item.locale }}</span>
      </template>

      <template slot="width" slot-scope="props">
        <span v-if="props.item.editing">
          <field :value="props.item.width" v-model="props.item.width" />
        </span>
        <span v-else>{{ props.item.width }}</span>
      </template>

      <template slot="height" slot-scope="props">
        <span v-if="props.item.editing">
          <field :value="props.item.height" v-model="props.item.height" />
        </span>
        <span v-else>{{ props.item.height }}</span>
      </template>
      <template slot="render_video_duration" slot-scope="props">
        <span v-if="props.item.editing">
          <field :value="props.item.render_video_duration" :type="'number'" v-model="props.item.render_video_duration" />
        </span>
        <span v-else>{{ props.item.render_video_duration }}</span>
      </template>

      <template slot="render_video_fps" slot-scope="props">
        <span>
          <span v-if="props.item.editing">
            <dropdown :options="fpsOptions" v-model="props.item.render_video_fps"
              :current="props.item.render_video_fps" />
          </span>
          <span v-else>{{ props.item.render_video_fps }}</span>
        </span>
      </template>
      <template slot="render_video_format" slot-scope="props">
        <span>
          <span v-if="props.item.editing">
            <dropdown :options="videoFormatOptions" v-model="props.item.render_video_format"
              :current="props.item.render_video_format" />
          </span>
          <span v-else>{{ props.item.render_video_format }}</span>
        </span>
      </template>
      <template slot="date_created" slot-scope="props">
        <span>{{ props.item.date_created.value | formatDate }}</span>
        <span>{{ props.item.date_created.value | formatTime }}</span>
      </template>

      <template slot="status" slot-scope="props" class="center-container">
        <span v-if="props.item.status === 'error'" class="custom_align">
          <tooltip :text="props.item.status_message" :position="getPropsIndex(props.item)">
            <span :class="statusCss(props.item.status)">
              <i class="fa fa-exclamation-triangle"></i>
              {{ props.item.status }}
            </span>
          </tooltip>
        </span>
        <span v-else-if="props.item.status === 'feedback_submitted'" class="custom_align">
          <tooltip :text="props.item.status" :position="getPropsIndex(props.item)">
            <span :class="statusCss(props.item.status)">
              {{ props.item.status }}
            </span>
          </tooltip>
        </span>
        <span v-else-if="props.item.status === 'approved'" class="custom_align">
          <tooltip :text="props.item.status" :position="getPropsIndex(props.item)">
            <span :class="statusCss(props.item.status)">
              {{ props.item.status }}
            </span>
          </tooltip>
        </span>
        <span v-else-if="props.item.status === 'under_review'" class="custom_align">
          <tooltip :text="getReviewLinkName(props.item)" :position="getPropsIndex(props.item)">
            <span :class="statusCss(props.item.status)">
              {{ props.item.status }}
            </span>
          </tooltip>
        </span>
        <span v-else>
          <span :class="statusCss(props.item.status)">
            {{ props.item.status.replace(/_/g, ' ') }}
          </span>
        </span>
      </template>

      <template slot="master" slot-scope="props">
        <span>
          <!--              <span v-if="props.item.editing">-->
          <!--                <dropdown :options="masterOptions" v-model="props.item.master" :current="props.item.master"/>-->
          <!--              </span>-->
          <!--              <span v-else>{{ props.item.master }}</span>-->
          <span>{{ props.item.master }}</span>
        </span>
      </template>

      <template slot="composition" slot-scope="props">
        <span>
          <span v-if="props.item.editing">
            <dropdown :options="compositionOptions" v-model="props.item.composition" :current="props.item.composition" />
          </span>
          <span v-else>{{ props.item.composition }}</span>
        </span>
      </template>

      <template slot="encoding_profile" slot-scope="props">
        <span>
          <span v-if="props.item.editing" class="dropdown">
            <v-select label="display_name" :options="encodingProfileData" v-model="props.item.encoding_profile" multiple>
            </v-select>
          </span>
          <!-- <span v-else>{{ displayEncodingProfiles(props.item.encoding_profile) }}</span> -->
          <span v-else>
            <ul>
              <li v-for="profile in props.item.encoding_profile" :key="profile.id">{{ profile.display_name }}</li>
            </ul>
          </span>
        </span>
      </template>

      <template v-for="(field, index) in tableFields" :slot="field"  slot-scope="props">
        <span :key="index">
          <span v-if="tableFieldTypes[index] === 'image' || tableFieldTypes[index] === 'audio'" class="imageHolder">
            <span v-if="props.item.editing" class="creativeUnitTable__imageField" @click="updateImage"
              data-modalType="assetLibrary" :data-modalID="props.item.id" :data-modalField="field"
              :data-modalFieldType="tableFieldTypes[index]">
              <span v-if="!props.item.fields[field]">
                <span class="item"><i aria-hidden="true" class="fa fa-plus"></i> Add Asset</span>
              </span>
              <span v-else>
                <span class="thumb item"><img :src="thumbPath(props.item.fields[field])"
                    onerror="this.style.display='none'" /></span>
                <span class="item"><i>{{ props.item.fields[field] }}</i></span>
              </span>
            </span>
            <span v-else>
              <span class="thumb item">
                <img :src="thumbPath(props.item.fields[field])" onerror="this.style.display='none'" />
              </span>
              <span class="item">
                <i>{{ props.item.fields[field] }}</i>
              </span>
            </span>
          </span>
          <span v-else-if="tableFieldTypes[index] === 'video'" class="imageHolder">
            <span v-if="props.item.editing" class="creativeUnitTable__imageField" @click="updateImage"
              data-modalType="assetLibrary" :data-modalID="props.item.id" :data-modalField="field"
              :data-modalFieldType="tableFieldTypes[index]">
              <span v-if="!props.item.fields[field]">
                <span class="item"><i aria-hidden="true" class="fa fa-plus"></i> Add Video</span>
              </span>
              <span v-else>
                <span class="thumb item"><img :src="videoThumbPath(props.item.fields[field])"
                    onerror="this.style.display='none'" /></span>
                <span class="item"><i>{{ props.item.fields[field] }}</i></span>
              </span>
            </span>
            <span v-else>
              <span class="thumb item">
                <img :src="videoThumbPath(props.item.fields[field])" onerror="this.style.display='none'" />
              </span>
              <span class="item">
                <i>{{ props.item.fields[field] }}</i>
              </span>
            </span>
          </span>
          <span v-else-if="tableFieldTypes[index] === 'yesno'">
            <span v-if="props.item.editing">
              <dropdown :options="yesNoOptions" v-model="props.item.fields[field]" :current="props.item.fields[field]" />
            </span>
            <span v-else>
              <slot v-if="table.yesNoOptions[props.item.fields[field]]">
                {{ table.yesNoOptions[props.item.fields[field]]['name'] }}
              </slot>
            </span>
          </span>
          <span
            v-else-if="tableFieldTypes[index] === 'constrain' || tableFieldTypes[index] === 'boolean' || tableFieldTypes[index] === 'keepaspect'">
            <span v-if="props.item.editing">
              <dropdown :options="booleanOptions" v-model="props.item.fields[field]"
                :current="props.item.fields[field]" />
            </span>
            <span v-else>
              <slot v-if="table.booleanOptions[props.item.fields[field]]">
                {{ table.booleanOptions[props.item.fields[field]]['name'] }}
              </slot>
            </span>
          </span>
          <span v-else-if="tableFieldTypes[index] === 'alignment'">
            <span v-if="props.item.editing">
              <dropdown :options="alignmentOptions" v-model="props.item.fields[field]"
                :current="props.item.fields[field]" />
            </span>
            <span v-else>
              <slot v-if="table.alignmentOptions[props.item.fields[field]]">
                {{ table.alignmentOptions[props.item.fields[field]]['name'] }}
              </slot>
            </span>
          </span>
          <span v-else-if="tableFieldTypes[index] === 'color'">
            <span v-if="props.item.editing">
              <color-picker v-model="props.item.fields[field]" :color="props.item.fields[field]"
                :creativeType='creative.type' />
            </span>
            <span v-else>
              <slot v-if="props.item.fields[field]">
                <div class="colorPicker">{{ props.item.fields[field] }}<span
                    :style="{ backgroundColor: props.item.fields[field] }">&nbsp;</span></div>
              </slot>
              <slot v-else>
                No color has been set.
              </slot>
            </span>
          </span>
          <span v-else>
            <span v-if="props.item.editing"><text-tag :value="props.item.fields[field]"
                v-model="props.item.fields[field]" /></span>
            <span v-else>{{ formatTextString(props.item.fields[field]) }}</span>
          </span>
        </span>
      </template>
    </slot-table>
  </div>
</template>

<script>
import SlotTable from '@/components/Table/table'
import ButtonCta from '@/components/Button/button'
import Heading from '@/components/Heading/heading'
import Field from '@/components/Form/Field/field'
import SwitchButton from '@/components/SwitchButton/switch-button'
import Checkbox from '@/components/Form/Checkbox/checkbox'
import TextTag from '@/components/Form/TextTag/text-tag'
import Dropdown from '@/components/Form/Dropdown/dropdown'
import Tooltip from '@/components/Tooltip/tooltip'
import ColorPicker from '@/components/ColorPicker/color-picker'
import ModalAssetLibrary from '@/components/Modal/assets/modal-asset-library'
import vSelect from 'vue-select'

import { modalMixin } from '@/mixins/modalMixin'

import { API_URL, MEDIA_URL, AWS_S3_BUCKET } from '@/config'

import { findIndex, propEq } from 'ramda'

export default {
  name: 'CreativeUnitTable',
  components: {
    SlotTable,
    ButtonCta,
    Field,
    SwitchButton,
    Checkbox,
    Heading,
    ModalAssetLibrary,
    TextTag,
    Dropdown,
    Tooltip,
    ColorPicker,
    vSelect
  },
  mixins: [modalMixin],
  props: {
    tableData: Array,
    tableFields: Array,
    tableFieldTypes: Array,
    tableSortList: Array,
    columnData: Object,
    mastersData: Array,
    compositionData: Array,
    encodingProfileData: Array,
    creative: Object,
    tableAllowedSortColumns: { type: Array, default: null },
    tableActiveSortData: { type: Array, default: null }
  },
  data: () => ({
    table: {
      columns: {
        is_editable: 'allCheckboxes',
        name: 'Name',
        type: 'Type',
        limit: 'Limit',
        is_hard_limit: 'Hard&nbsp;Limit',
        defaultValue: 'Default Value'
      },
      statusKeys: {
        rendered: 'success',
        under_review: 'warning',
        edited: 'info',
        error: 'danger',
        queued: 'warning',
        approved: 'approved',
        feedback_submitted: 'feedback_submitted'
      },
      booleanOptions: {
        0: {
          name: 'FALSE',
          value: 0
        },
        1: {
          name: 'TRUE',
          value: 1
        }
      },
      yesNoOptions: {
        'no': {
          name: 'no',
          value: 'no'
        },
        'yes': {
          name: 'yes',
          value: 'yes'
        }
      },
      alignmentOptions: {
        'left': {
          name: 'left',
          value: 'left'
        },
        'center': {
          name: 'center',
          value: 'center'
        },
        'right': {
          name: 'right',
          value: 'right'
        }
      },
      videoFormatOptions: {
        'mp4': {
          name: 'mp4',
          value: 'mp4'
        },
        'gif': {
          name: 'gif',
          value: 'gif'
        }
      },
      fpsOptions: {
        '12': {
          name: '12',
          value: '12'
        },
        '15': {
          name: '15',
          value: '15'
        },
        '24': {
          name: '24',
          value: '24'
        },
        '29': {
          name: '29',
          value: '29'
        },
        '30': {
          name: '30',
          value: '30'
        },
        '50': {
          name: '50',
          value: '50'
        },
        '60': {
          name: '60',
          value: '60'
        }
      }
    },
    lastChecked: null,
    aeFlag: false
  }),
  computed: {
    masterOptions () {
      const optionsArray = []
      this.mastersData.forEach((item) => {
        const option = {
          name: item,
          value: item
        }
        optionsArray.push(option)
      })
      return optionsArray
    },
    compositionOptions () {
      const optionsArray = []
      this.compositionData.forEach((item) => {
        const option = {
          name: item,
          value: item
        }
        optionsArray.push(option)
      })
      return optionsArray
    },
    encodingProfileOptions () {
      return this.encodingProfileData.map(item => ({
        label: item.display_name,
        code: item.name
      }))
    },
    booleanOptions () {
      const optionsArray = []
      for (var key in this.table.booleanOptions) {
        const option = {
          name: this.table.booleanOptions[key].name,
          value: this.table.booleanOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    yesNoOptions () {
      const optionsArray = []
      for (var key in this.table.yesNoOptions) {
        const option = {
          name: this.table.yesNoOptions[key].name,
          value: this.table.yesNoOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    alignmentOptions () {
      const optionsArray = []
      for (var key in this.table.alignmentOptions) {
        const option = {
          name: this.table.alignmentOptions[key].name,
          value: this.table.alignmentOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    videoFormatOptions () {
      const optionsArray = []
      for (var key in this.table.videoFormatOptions) {
        const option = {
          name: this.table.videoFormatOptions[key].name,
          value: this.table.videoFormatOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    fpsOptions () {
      const optionsArray = []
      for (var key in this.table.fpsOptions) {
        const option = {
          name: this.table.fpsOptions[key].name,
          value: this.table.fpsOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    }
  },
  mounted () {
    this.newData = this.tableData
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    checkIfIsAdmin () {
      return this.$store.state.profile.profileUser.role === 1
    },
    onStatusChangeToEdited (e) {
      this.$emit('onStatusChangeToEdited', e)
    },
    getEditingEPStyles (status) {
      if (status) {
        return {
          zIndex: 100,
          position: 'absolute'
        }
      }
      return {}
    },
    shouldShowCallToActions (item, validateStateQueued) {
      if (item.readOnly) return false
      if (validateStateQueued && item.status === 'queued') return false
      return true
    },
    toggleParentCheckboxes (toggle) {
      this.$emit('onToggleParentCheckboxes', toggle)
    },
    thumbPath (filename) {
      if (this.creative.type === 'after-effects') {
        return AWS_S3_BUCKET + this.creative.campaign.client.slug.toLowerCase() + '/' + this.creative.campaign.hash_id + '/' + this.creative.hash_id + '/_assets/' + filename
      } else {
        return MEDIA_URL + this.creative.campaign.client.slug.toLowerCase() + '/' + this.creative.campaign.hash_id + '/' + this.creative.hash_id + '/_assets/' + filename + '?ts=' + Math.round(+new Date() / 1000)
      }
    },
    videoThumbPath (filename) {
      if (this.creative.type === 'after-effects') {
        return AWS_S3_BUCKET + this.creative.campaign.client.slug.toLowerCase() + '/' + this.creative.campaign.hash_id + '/' + this.creative.hash_id + '/_thumbnails/' + filename + '_thumb.jpeg'
      } else {
        return MEDIA_URL + this.creative.campaign.client.slug.toLowerCase() + '/' + this.creative.campaign.hash_id + '/' + this.creative.hash_id + '/_thumbnails/' + filename + '_thumb.jpeg' + '?ts=' + Math.round(+new Date() / 1000)
      }
    },
    previewUrl (previewUrl) {
      return API_URL + previewUrl
    },
    editRow (e) {
      this.$emit('onEditRow', e)
    },
    deleteRow (e) {
      this.$emit('onDeleteRow', e)
    },
    copyRow (e) {
      this.$emit('onCopyRow', e)
    },
    cancelRow (e) {
      console.log('Child cancel row.')
      this.$emit('onCancelRow', e)
    },
    launchPreview (e) {
      let offsets = []
      let isFirst = true
      this.tableData.every(el => {
        if (isFirst && el.id === e.currentTarget.dataset.previewid) {
          isFirst = false
          return false
        } else if (isFirst && el.id !== e.currentTarget.dataset.previewid && (el.status === 'rendered' || el.status === 'under_review' || el.status === 'approved' || el.status === 'feedback_submitted') && this.creative.type === 'after-effects') {
          offsets.push(el.encoding_profile.length - 1)
        }
        return true
      })
      e.currentTarget.dataset.offset = offsets.reduce((partialSum, a) => partialSum + a, 0)
      this.$emit('onPreview', e)
    },
    statusCss (status) {
      let labelType = 'default'
      if (status !== 'new') {
        labelType = this.table.statusKeys[status]
      }
      return `label label-${labelType} text-center text-capitalize`
    },
    updateImage (e) {
      this.$emit('onUpdateImage', e)
    },
    formatTextString (string) {
      return string ? string.replace(/\\n/g, '\n') : ''
    },
    handleResize: function (event) {
      // On resize make the scroll area size the same as the table area
    },
    addSort: function (field) {
      this.$emit('onSortTableData', field)
    },
    dispatchSortEvent: function (sortData) {
      this.$emit('onDispatchSortEvent', sortData)
    },
    getReviewLinkName (item) {
      let value = ''
      if (item.review_link_snapshot) {
        value = item.review_link_snapshot.name
      }
      return value
    },
    getPropsIndex (item) {
      let i = findIndex(propEq('id', item.id))(this.tableData)
      if (i === 0) {
        return 'bottom'
      } else {
        return 'right'
      }
    },
    groupSelectCheckboxes (e) {
      if (this.lastChecked && e.shiftKey) {
        // Get indexes of the two clicked checkboxes
        const previousId = this.lastChecked.id.replace('checkbox_', '')
        let i = findIndex(propEq('id', previousId))(this.tableData)
        const currentId = e.target.id.replace('checkbox_', '')
        let j = findIndex(propEq('id', currentId))(this.tableData)

        let checkOrUncheck = e.target.checked

        // up or down selection
        let low = i
        let high = j
        if (i > j) {
          low = j
          high = i
        }

        // Set checked state
        for (let c = 0; c < this.tableData.length; c++) {
          if (low <= c && c <= high) {
            this.tableData[c].selected = checkOrUncheck
          }
        }
        this.lastChecked = null
      }
      this.lastChecked = e.target // Store this input as the last clicked
    },
    aePreviewUrl (previewUrl) {
      // return true
      if (previewUrl == null) {
        return false
      }
      this.aeFlag = true
      previewUrl.map((url) => {
        if (!url) {
          this.aeFlag = false
        }
      })
      return this.aeFlag
    },
    displayEncodingProfiles (eps) {
      let data = []
      Object.keys(eps).forEach(key => {
        data.push(eps[key].display_name || '')
      })
      return data
    }
  }
}
</script>
<style scoped>
.selector {
  --vs-controls-color: #2c3247;
  /*--vs-border-color: #664cc3;*/

  --vs-dropdown-bg: #2c3247;
  --vs-dropdown-color: #ffffff;
  --vs-dropdown-option-color: #ffffff;

  --vs-selected-bg: #41b883;
  --vs-selected-color: #ffffff;

  --vs-search-input-color: #eeeeee;

  --vs-dropdown-option--active-bg: #41b883;
  --vs-dropdown-option--active-color: #fffff;
}
</style>
<style lang="scss">
.label-approved {
  background-color: #006400;
}

.label-feedback_submitted {
  background-color: #b81712;
}

.creativeUnitTable tbody.scrollContent td {
  word-break: break-word;
}

:root {
  --vs-colors--light: rgba(60, 60, 60, 0.5);
  --vs-colors--dark: #333;
  --vs-colors--darkest: rgba(0, 0, 0, 0.15);
  --vs-search-input-color: inherit;
  --vs-search-input-bg: #fff;
  --vs-search-input-placeholder-color: inherit;
  --vs-font-size: 1rem;
  --vs-line-height: 1.4;
  --vs-state-disabled-bg: #f8f8f8;
  --vs-state-disabled-color: var(--vs-colors--light);
  --vs-state-disabled-controls-color: var(--vs-colors--light);
  --vs-state-disabled-cursor: not-allowed;
  --vs-border-color: var(--vs-colors--lightest);
  --vs-border-width: 1px;
  --vs-border-style: solid;
  --vs-border-radius: 4px;
  --vs-actions-padding: 4px 6px 0 3px;
  --vs-controls-color: var(--vs-colors--light);
  --vs-controls-size: 1;
  --vs-controls--deselect-text-shadow: 0 1px 0 #fff;
  --vs-selected-bg: #f0f0f0;
  --vs-selected-color: var(--vs-colors--dark);
  --vs-selected-border-color: var(--vs-border-color);
  --vs-selected-border-style: var(--vs-border-style);
  --vs-selected-border-width: var(--vs-border-width);
  --vs-dropdown-bg: #fff;
  --vs-dropdown-color: inherit;
  --vs-dropdown-z-index: 1000;
  --vs-dropdown-min-width: 160px;
  --vs-dropdown-max-height: 350px;
  --vs-dropdown-box-shadow: 0px 3px 6px 0px var(--vs-colors--darkest);
  --vs-dropdown-option-bg: #000;
  --vs-dropdown-option-color: var(--vs-dropdown-color);
  --vs-dropdown-option-padding: 3px 20px;
  --vs-dropdown-option--active-bg: #5897fb;
  --vs-dropdown-option--active-color: #fff;
  --vs-dropdown-option--deselect-bg: #fb5858;
  --vs-dropdown-option--deselect-color: #fff;
  --vs-transition-timing-function: cubic-bezier(1, -0.115, 0.975, 0.855);
  --vs-transition-duration: 150ms
}

.v-select {
  font-family: inherit;
  position: relative
}

.v-select,
.v-select * {
  box-sizing: border-box
}

:root {
  --vs-transition-timing-function: cubic-bezier(1, 0.5, 0.8, 1);
  --vs-transition-duration: 0.15s
}

@-webkit-keyframes vSelectSpinner {
  0% {
    transform: rotate(0deg)
  }

  to {
    transform: rotate(1turn)
  }
}

@keyframes vSelectSpinner {
  0% {
    transform: rotate(0deg)
  }

  to {
    transform: rotate(1turn)
  }
}

.vs__fade-enter-active,
.vs__fade-leave-active {
  pointer-events: none;
  transition: opacity var(--vs-transition-duration) var(--vs-transition-timing-function)
}

.vs__fade-enter,
.vs__fade-leave-to {
  opacity: 0
}

:root {
  --vs-disabled-bg: var(--vs-state-disabled-bg);
  --vs-disabled-color: var(--vs-state-disabled-color);
  --vs-disabled-cursor: var(--vs-state-disabled-cursor)
}

.vs--disabled .vs__clear,
.vs--disabled .vs__dropdown-toggle,
.vs--disabled .vs__open-indicator,
.vs--disabled .vs__search,
.vs--disabled .vs__selected {
  background-color: var(--vs-disabled-bg);
  cursor: var(--vs-disabled-cursor)
}

.v-select[dir=rtl] .vs__actions {
  padding: 0 3px 0 6px
}

.v-select[dir=rtl] .vs__clear {
  margin-left: 6px;
  margin-right: 0
}

.v-select[dir=rtl] .vs__deselect {
  margin-left: 0;
  margin-right: 2px
}

.v-select[dir=rtl] .vs__dropdown-menu {
  text-align: right
}

.vs__dropdown-toggle {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: var(--vs-search-input-bg);
  border: var(--vs-border-width) var(--vs-border-style) var(--vs-border-color);
  border-radius: var(--vs-border-radius);
  display: flex;
  padding: 0 0 4px;
  white-space: normal
}

.vs__selected-options {
  display: flex;
  flex-basis: 100%;
  flex-grow: 1;
  flex-wrap: wrap;
  padding: 0 2px;
  position: relative
}

.vs__actions {
  align-items: center;
  display: flex;
  padding: var(--vs-actions-padding)
}

.vs--searchable .vs__dropdown-toggle {
  cursor: text
}

.vs--unsearchable .vs__dropdown-toggle {
  cursor: pointer
}

.vs--open .vs__dropdown-toggle {
  border-bottom-color: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0
}

.vs__open-indicator {
  fill: var(--vs-controls-color);
  transform: scale(var(--vs-controls-size));
  transition: transform var(--vs-transition-duration) var(--vs-transition-timing-function);
  transition-timing-function: var(--vs-transition-timing-function)
}

.vs--open .vs__open-indicator {
  transform: rotate(180deg) scale(var(--vs-controls-size))
}

.vs--loading .vs__open-indicator {
  opacity: 0
}

.vs__clear {
  fill: var(--vs-controls-color);
  background-color: transparent;
  border: 0;
  cursor: pointer;
  margin-right: 8px;
  padding: 0
}

.vs__dropdown-menu {
  background: var(--vs-dropdown-bg);
  border: var(--vs-border-width) var(--vs-border-style) var(--vs-border-color);
  border-radius: 0 0 var(--vs-border-radius) var(--vs-border-radius);
  border-top-style: none;
  box-shadow: var(--vs-dropdown-box-shadow);
  box-sizing: border-box;
  color: var(--vs-dropdown-color);
  display: block;
  left: 0;
  list-style: none;
  margin: 0;
  max-height: var(--vs-dropdown-max-height);
  min-width: var(--vs-dropdown-min-width);
  overflow-y: auto;
  padding: 5px 0;
  position: absolute;
  text-align: left;
  top: calc(100% - var(--vs-border-width));
  width: 100%;
  z-index: var(--vs-dropdown-z-index)
}

.vs__no-options {
  text-align: center
}

.vs__dropdown-option {
  clear: both;
  color: var(--vs-dropdown-option-color);
  cursor: pointer;
  display: block;
  line-height: 1.42857143;
  padding: var(--vs-dropdown-option-padding);
  white-space: nowrap
}

.vs__dropdown-option--highlight {
  background: var(--vs-dropdown-option--active-bg);
  color: var(--vs-dropdown-option--active-color)
}

.vs__dropdown-option--deselect {
  background: var(--vs-dropdown-option--deselect-bg);
  color: var(--vs-dropdown-option--deselect-color)
}

.vs__dropdown-option--disabled {
  background: var(--vs-state-disabled-bg);
  color: var(--vs-state-disabled-color);
  cursor: var(--vs-state-disabled-cursor)
}

.vs__selected {
  align-items: center;
  background-color: var(--vs-selected-bg);
  border: var(--vs-selected-border-width) var(--vs-selected-border-style) var(--vs-selected-border-color);
  border-radius: var(--vs-border-radius);
  color: var(--vs-selected-color);
  display: flex;
  line-height: var(--vs-line-height);
  margin: 4px 2px 0;
  padding: 0 .25em;
  z-index: 0
}

.vs__deselect {
  fill: var(--vs-controls-color);
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  margin-left: 4px;
  padding: 0;
  text-shadow: var(--vs-controls--deselect-text-shadow)
}

.vs--single .vs__selected {
  background-color: transparent;
  border-color: transparent
}

.vs--single.vs--loading .vs__selected,
.vs--single.vs--open .vs__selected {
  opacity: .4;
  position: absolute
}

.vs--single.vs--searching .vs__selected {
  display: none
}

.vs__search::-webkit-search-cancel-button {
  display: none
}

.vs__search::-ms-clear,
.vs__search::-webkit-search-decoration,
.vs__search::-webkit-search-results-button,
.vs__search::-webkit-search-results-decoration {
  display: none
}

.vs__search,
.vs__search:focus {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  border: 1px solid transparent;
  border-left: none;
  box-shadow: none;
  color: var(--vs-search-input-color);
  flex-grow: 1;
  font-size: var(--vs-font-size);
  line-height: var(--vs-line-height);
  margin: 4px 0 0;
  max-width: 100%;
  outline: none;
  padding: 0 7px;
  width: 0;
  z-index: 1
}

.vs__search::-moz-placeholder {
  color: var(--vs-search-input-placeholder-color)
}

.vs__search:-ms-input-placeholder {
  color: var(--vs-search-input-placeholder-color)
}

.vs__search::placeholder {
  color: var(--vs-search-input-placeholder-color)
}

.vs--unsearchable .vs__search {
  opacity: 1
}

.vs--unsearchable:not(.vs--disabled) .vs__search {
  cursor: pointer
}

.vs--single.vs--searching:not(.vs--open):not(.vs--loading) .vs__search {
  opacity: .2
}

.vs__spinner {
  align-self: center;
  -webkit-animation: vSelectSpinner 1.1s linear infinite;
  animation: vSelectSpinner 1.1s linear infinite;
  border: .9em solid hsla(0, 0%, 39%, .1);
  border-left-color: rgba(60, 60, 60, .45);
  font-size: 5px;
  opacity: 0;
  overflow: hidden;
  text-indent: -9999em;
  transform: translateZ(0) scale(var(--vs-controls--spinner-size, var(--vs-controls-size)));
  transition: opacity .1s
}

.vs__spinner,
.vs__spinner:after {
  border-radius: 50%;
  height: 5em;
  transform: scale(var(--vs-controls--spinner-size, var(--vs-controls-size)));
  width: 5em
}

.vs--loading .vs__spinner {
  opacity: 1
}

$defaultTdWidth: 100px;
$headerHeight: 260px; //Height of header area above table this is not dynamic, its hard coded for now.
$cellWidths: 400px;
$scrollHeight: 550px; //Min hiehgt to get htis working

.tableFix {
  width: 0% !important;
}

.custom_align{
  display: flex;
  justify-content: center;
  align-items: center;
}

.creativeUnitTable {
  overflow-x: auto;
  overflow-y: auto;
  max-width: 100%;
  margin: 0px 0 10px;
  height:calc(80vh - #{$headerHeight});
  background-color: #FFF;

  &__table {
    margin-bottom: 0;
  }

  .form-control {
    background-color: transparent;
  }

  & .colorPicker {
    border: 1px solid #ccc;
    background-color: #eee;
    border-radius: 5px;
    width: 231px;
    padding: 4px;
    text-align: left;
    margin: 0 auto;
    padding: 10px 12px;
    font-size: 14px;
    font-weight: normal;
    line-height: 1;

    & span {
      border: 1px solid #ccc;
      height: 16px;
      width: 16px;
      display: inline-block;
      // float: right;
    }
  }

  &__imageField {
    cursor: pointer;
  }

  img {
    max-width: 50px;
    max-height: 50px;
  }

  .imageHolder {
    display: flex;
    min-height: 50px;
    align-items: center;
    justify-content: center;

    & .item {
      display: inline-flex;
    }

    & .thumb {
      justify-content: center;
      border: 1px solid black;
      display: contents;

      & img {
        max-height: 50px;
        max-width: 50px;
        padding: 0;
      }
    }
  }
}

/* Start of external code used for fixed headers */
/* define height and width of scrollable area. Add 16px to width for scrollbar          */
/* Reset overflow value to hidden for all non-IE browsers. */
html>body .creativeUnitTable div.tableContainer {
  overflow: hidden;
}

/* define width of table. IE browsers only                 */
.creativeUnitTable div.tableContainer table {
  float: left;
}

/* set table header to a fixed position. WinIE 6.x only                                       */
/* In WinIE 6.x, any element with a position property set to relative and is a child of       */
/* an element that has an overflow property set, the relative value translates into fixed.    */
/* Ex: parent element DIV with a class of tableContainer has an overflow property set to auto */
.creativeUnitTable thead.fixedHeader tr {
  position: relative
}

/* set THEAD element to have block level attributes. All other non-IE browsers            */
/* this enables overflow to work on TBODY element. All other non-IE, non-Mozilla browsers */
html>body .creativeUnitTable thead.fixedHeader tr {
  display: block
}

/* make the TH elements pretty */
.creativeUnitTable thead.fixedHeader th {
  background: #2c3247;
  color: white;
}

/* make the A elements pretty. makes for nice clickable headers                */
.creativeUnitTable thead.fixedHeader a,
.creativeUnitTable thead.fixedHeader a:link,
.creativeUnitTable thead.fixedHeader a:visited {
  color: #FFF;
  display: block;
  text-decoration: none;
  width: 100%
}

/* make the A elements pretty. makes for nice clickable headers                */
/* WARNING: swapping the background on hover may cause problems in WinIE 6.x   */
.creativeUnitTable thead.fixedHeader a:hover {
  color: #FFF;
  display: block;
  text-decoration: underline;
  width: 100%
}

/* define the table content to be scrollable                                              */
/* set TBODY element to have block level attributes. All other non-IE browsers            */
/* this enables overflow to work on TBODY element. All other non-IE, non-Mozilla browsers */
/* induced side effect is that child TDs no longer accept width: auto                     */
html>body .creativeUnitTable tbody.scrollContent {
  display: block;
  //max-height:calc(80vh - #{$headerHeight} - 50px);
  overflow: visible;
  width: 100%
}

.creativeUnitTable tbody.scrollContent td {
  word-break: break-word;
}

/* Make all the tds and Ths equal widths */
.creativeUnitTable th,
.creativeUnitTable td {
  max-width: $cellWidths;
  min-width: $cellWidths;
}

/* Set the first second and third ones to be smaller specific widths */
//Checkbox
.creativeUnitTable th:nth-child(1),
.creativeUnitTable td:nth-child(1) {
  max-width: 30px;
  min-width: 30px;
  padding-left: 12px;
}

//Buttons
.creativeUnitTable th:nth-child(2),
.creativeUnitTable td:nth-child(2) {
  max-width: 151px;
  min-width: 151px;
}

//Status
.creativeUnitTable th:nth-child(3),
.creativeUnitTable td:nth-child(3) {
  max-width: 150px;
  min-width: 150px;
}

.creativeUnitTable td:nth-child(3) {
  text-align: center;
}

//Date
.creativeUnitTable th:nth-child(4),
.creativeUnitTable td:nth-child(4) {
  max-width: 135px;
  min-width: 135px;
}

// Master
.creativeUnitTable th:nth-child(5),
.creativeUnitTable td:nth-child(5) {
  max-width: 150px;
  min-width: 150px;
}

//Locale
.creativeUnitTable th:nth-child(6),
.creativeUnitTable td:nth-child(6) {
  max-width: 200px;
  min-width: 200px;
}

// height and width
.creativeUnitTable th:nth-child(7),
.creativeUnitTable td:nth-child(7),
.creativeUnitTable th:nth-child(8),
.creativeUnitTable td:nth-child(8) {
  max-width: 150px;
  min-width: 150px;
}

/* make TD elements pretty. Provide alternating classes for striping the table */
/* http://www.alistapart.com/articles/zebratables/                             */
.creativeUnitTable tbody.scrollContent td,
.creativeUnitTable tbody.scrollContent tr.normalRow td {
  border-bottom: none;
  border-left: none;
}

/* ENSURE Contents waraps in TD */
.creativeUnitTable td {
  word-wrap: break-word;
}

.scrollContent tr td td {
  width: 250px;
  overflow-wrap: anywhere;
}

table tr td:nth-child(2) {
  width: 250px;
  overflow-wrap: anywhere;
}
</style>



// WEBPACK FOOTER //
// src/components/CreativeUnitTable/creative-unit-table.vue